/*
  Was der Fraktion gehört — die eine Hälfte der Daten.

  Die Trennlinie des Projekts verläuft hier: Was von der Verwaltung kommt
  (Sitzung, Tagesordnung, Vorlagen), holt die App über den Server aus dem
  Ratsinformationssystem. Siehe `vomServer.js`. Was die Fraktion selbst
  erzeugt, steht in dieser Datei — im Prototyp noch als Beispiel, später auf
  dem eigenen Server.

  HARTE REGEL: Alles hier ist erfunden. Stadt Ellernbach, die Fraktion
  Bürgerforum und alle Vorlagentexte existieren nicht. Es dürfen niemals
  echte Personennamen oder echte nichtöffentliche Verwaltungsunterlagen in
  diese Datei gelangen.

  Erfundene *Mitglieder* stehen hier nicht mehr. Wer zur Fraktion gehört,
  sind die wirklichen Konten auf dem Server — sonst ließe sich im Betrieb
  nicht unterscheiden, wer etwas tatsächlich gesagt hat.

  Die Fraktion trägt bewusst keinen Parteinamen: Die Software wird allen
  Fraktionen eines Rates gezeigt und muss parteineutral wirken.

  Aufbau der Datei:
    1. eigeneStammdaten — Körperschaft, Fraktion, Volltexte der Vorlagen
    2. ergaenzungen     — was OParl nicht hergibt, ausdrücklich benannt
    3. baueEigeneTermine — hängt am Sitzungstermin, den die Verwaltung
       vorgibt, und wird daraus gerechnet

  Was die Fraktion selbst erzeugt — Notizen, Meinungsbild, Teilnahme,
  Aufgaben, Chat — steht nicht mehr hier, sondern auf dem Server. Diese Datei
  beschreibt nur noch die gespielte Kommune und ihre Unterlagen.
*/

import { dokumente } from "./dokumente";
import { heute, mitUhrzeit, plusTage } from "../hilfen/datum";

/* ---------------------------------------------------------------
   1. Stammdaten der Fraktion
   --------------------------------------------------------------- */

const koerperschaft = {
  id: "k-01",
  name: "Stadt Ellernbach",
  einwohner: 38400,
  kreis: "Kreis Warkenbrück",
};

/*
  Nur die Fraktion steht hier. Rat und Ausschüsse gehören der Verwaltung; ihre
  Namen kommen mit der Sitzung vom Server dazu.
*/
const gremien = {
  "g-fraktion": {
    id: "g-fraktion",
    name: "Fraktion Bürgerforum",
    kurz: "Fraktion",
    art: "Fraktion",
  },
};


/* ---------------------------------------------------------------
   2. Ergänzungen — was OParl nicht hergibt
   --------------------------------------------------------------- */

/*
  Vier Angaben fehlen in den Daten, die über die Schnittstelle hereinkommen.
  Sie stehen hier ausdrücklich beisammen, damit niemand sie später für
  Serverdaten hält:

  1. `kurztitel` — die Kachel auf der Startseite braucht eine kurze
     Beschriftung. OParl kennt nur den vollen Titel.
  2. `nichtoeffentlichGrund` — OParl sagt, dass ein Punkt nichtöffentlich ist,
     aber nie warum. Für die Fraktion ist gerade das die wichtige Angabe.
  3. `weitereVorlagen` — an Tagesordnungspunkt 6 hängt neben dem Antrag der
     Fraktion auch die Stellungnahme der Verwaltung. Die Schnittstelle nennt je
     Punkt nur eine Beratung.
  4. `beratungsstand` — ob eine Vorlage „in Beratung“ ist oder eine
     Stellungnahme „vorliegt“, steht in OParl nicht.

  In der echten Software wird das je Kommune anders zu lösen sein: teils aus
  weiteren OParl-Feldern, teils aus dem, was die Fraktion selbst pflegt.
  Geschlüsselt wird über das Aktenzeichen ohne Fassung beziehungsweise über die
  Kennung des Tagesordnungspunkts.
*/

/*
  Sitzungen sind über die Wahlperiode durchnummeriert.

  Für eine Fraktion ist das keine Zierde, sondern die Adresse eines Vorgangs:
  „Das haben wir in der 7. Sitzung beschlossen“ steht so in jeder
  Niederschrift. Ohne die Nummer weiß man bei einem Beitrag von vor einem
  halben Jahr nicht, zu welcher Sitzung er gehörte — der Titel des
  Tagesordnungspunkts allein sagt es nicht, weil derselbe Vorgang mehrfach
  auf die Tagesordnung kommt.

  OParl liefert die Nummer nicht. Sie wird deshalb aus dem Termin gerechnet:
  Der Ausschuss tagt im Vierwochentakt seit dem Beginn der Wahlperiode. Damit
  schreibt sie sich von selbst fort, wenn die gespielte Verwaltung ihre
  Sitzung um vier Wochen weiterschiebt — sonst stünde in einem halben Jahr
  immer noch dieselbe Nummer da.

  In der echten Software kommt die Nummer aus dem Ratsinformationssystem oder
  wird beim Anlegen der Fraktion einmal gesetzt.
*/
export const WAHLPERIODE_BEGINN = new Date("2025-11-01T00:00:00Z");

const VIER_WOCHEN = 28 * 24 * 3600 * 1000;

export function sitzungsnummer(beginn) {
  const abstand = new Date(beginn) - WAHLPERIODE_BEGINN;
  if (!Number.isFinite(abstand) || abstand < 0) return 1;
  return Math.floor(abstand / VIER_WOCHEN) + 1;
}

export const ergaenzungen = {
  vorlagen: {
    "VL/2026/184": { kurztitel: "Bebauungsplan Nr. 47", beratungsstand: "in Beratung" },
    "VL/2026/191": { kurztitel: "Grundschule Nord, Vergabe", beratungsstand: "in Beratung" },
    "VL/2026/176": { kurztitel: "Radverkehrskonzept", beratungsstand: "in Beratung" },
    "A/2026/033": { kurztitel: "Antrag Tempo 30", beratungsstand: "in Beratung" },
    "VL/2026/188": { kurztitel: "Stellungnahme Tempo 30", beratungsstand: "liegt vor" },
    "VL/2026/195": { kurztitel: "Kita-Gebührensatzung", beratungsstand: "in Beratung" },
  },

  tagesordnung: {
    "top-4": {
      nichtoeffentlichGrund:
        "Angaben zu einem laufenden Vergabeverfahren und zu Angebotspreisen einzelner Bieter.",
    },
    "top-6": { weitereVorlagen: ["vl-188"] },
  },
};

/* ---------------------------------------------------------------
   3. Die Fraktionssitzung und die Fristen
   --------------------------------------------------------------- */

/*
  Die vorbereitende Fraktionssitzung steht in keinem Ratsinformationssystem —
  sie ist kein Gremium im Sinne der Gemeindeordnung. Deshalb wird sie hier
  gebildet, und zwar aus dem Termin der Ausschusssitzung: immer der Dienstag
  davor, 18:30 Uhr. Verschiebt die Verwaltung die Sitzung, wandert die
  Fraktionssitzung mit.
*/
export function baueEigeneTermine(ausschussBeginn) {
  const ausschuss = new Date(ausschussBeginn);
  const fraktionsTag = plusTage(ausschuss, -2);

  return {
    ausschuss,
    fraktionssitzung: mitUhrzeit(fraktionsTag, 18, 30),
    fristStellplatz: plusTage(heute(), 1),
    fristAenderungsantrag: fraktionsTag,
    fristOrtstermin: plusTage(ausschuss, 2),
  };
}

export function fraktionssitzung(ausschussBeginn, ausschussId) {
  const termine = baueEigeneTermine(ausschussBeginn);
  return {
    id: "s-fraktion",
    gremiumId: "g-fraktion",
    beginn: termine.fraktionssitzung,
    ort: "Fraktionszimmer 2.14, Rathaus",
    art: "Fraktionssitzung",
    bereitetVor: ausschussId,
    hinweis:
      "Vorbereitende Sitzung der Fraktion. Kein Gremium im Sinne der Gemeindeordnung.",
  };
}


/*
  Die Hälfte der Stammdaten, die der Fraktion gehört. Die andere Hälfte —
  Sitzung, Tagesordnung, Vorlagen — kommt vom Server dazu; zusammengesetzt
  wird beides in `vomServer.js`.

  `dateien` sind die Volltexte der Vorlagen. Sie bleiben vorerst hier: Wanderten
  sie mit auf den Server, müssten Markierungen und Meinungsbilder im selben
  Zug mit, weil sie auf die Absatz-Kennungen dieser Texte zeigen. Siehe
  VEREINFACHUNGEN.md.
*/
export const eigeneStammdaten = {
  koerperschaft,
  gremien,
  dateien: dokumente,
};

/*
  Die Kanäle der Fraktion neben dem tagenden Ausschuss.

  Es gibt bewusst keinen Kanal je Tagesordnungspunkt. Ein Ausschuss besteht
  die ganze Wahlperiode, seine Themen kommen wieder — der Bebauungsplan
  Nr. 47 kommt als Auslegungsbeschluss, als Abwägung und als
  Satzungsbeschluss. In einem Kanal je Ausschuss steht das hintereinander in
  einem Verlauf; bei einem Kanal je Punkt zerfiele es in drei.

  Der Ausschuss ist der richtige Zuschnitt, weil er zwei Dinge gleichzeitig
  ist: eine feste Gruppe von Menschen und ein fester Sachbereich.

  Die Kanäle selbst sind leer — Inhalt entsteht durch Benutzung. Nur die
  Namen der Gremien stehen hier, und auch die kämen in der echten Software
  über OParl von der Verwaltung.
*/
export const weitereKanaele = [
  { id: "k-rat", name: "Rat der Stadt Ellernbach", art: "Rat", ergaenzt: true },
  { id: "k-fin", name: "Finanzausschuss", art: "Ausschuss", ergaenzt: true },
  { id: "k-jha", name: "Jugendhilfeausschuss", art: "Ausschuss", ergaenzt: true },
];

